// Helper functions for resolving chat avatars

export interface ConversationAvatarData {
  id: string;
  // Titles
  title?: string;
  // Types/categories (snake_case and camelCase for compatibility)
  conversation_type?: string;
  conversationType?: string;
  conversation_category?: string;
  conversationCategory?: string;
  // Avatars
  avatar_url?: string;
  avatarUrl?: string;
  profile_avatar_url?: string;
  // Company context
  company_id?: string;
  companyId?: string;
  company_name?: string;
  companyName?: string;
  company_logo_url?: string;
  companyLogoUrl?: string;
  // Role-based display data
  isCandidate?: boolean;
  // Optional nested relation shape when expanded via Supabase
  company_profiles?: { logo_url?: string };
}

/**
 * Resolves the appropriate avatar for a conversation based on user role
 * For candidates: shows company logos
 * For hirers: shows candidate avatars
 * For general conversations: uses participant avatars
 */
export function resolveConversationAvatar(conversation: ConversationAvatarData) {
  const isApplicationConv = isCompanyConversation(conversation);
  
  // For application conversations, avatar choice depends on user role
  if (isApplicationConv) {
    // If user is candidate (isCandidate flag set), show company logo
    if (conversation.isCandidate) {
      const companyLogo = conversation.company_logo_url || 
                         conversation.companyLogoUrl || 
                         conversation.company_profiles?.logo_url;
      
      if (companyLogo) {
        return {
          src: companyLogo,
          fallbackName: conversation.company_name || conversation.companyName || conversation.title,
          type: 'company' as const,
        };
      }
    }
    
    // If user is hirer or no specific company logo, use participant profile avatar
    const participantAvatar = conversation.profile_avatar_url || 
                             conversation.avatar_url || 
                             conversation.avatarUrl;
    
    return {
      src: participantAvatar,
      fallbackName: conversation.title,
      type: 'user' as const,
    };
  }

  // For non-application conversations, use participant avatar
  return {
    src: conversation.profile_avatar_url || conversation.avatar_url || conversation.avatarUrl,
    fallbackName: conversation.title,
    type: 'user' as const,
  };
}

/**
 * Determines if a conversation is company-related
 */
export function isCompanyConversation(conversation: ConversationAvatarData): boolean {
  const type = conversation.conversation_type || conversation.conversationType;
  const category = conversation.conversation_category || conversation.conversationCategory;
  return !!(
    conversation.company_id ||
    conversation.companyId ||
    type === 'application' ||
    category === 'hiring' ||
    category === 'application'
  );
}

/**
 * Gets company avatar props for the CompanyAvatar component
 * Returns props if user should see company avatar (i.e., user is candidate)
 */
export function getCompanyAvatarProps(conversation: ConversationAvatarData) {
  const avatar = resolveConversationAvatar(conversation);

  if (avatar.type === 'company') {
    return {
      src: avatar.src,
      name: avatar.fallbackName,
      alt: `${avatar.fallbackName} logo`,
      singleInitial: true,
    } as const;
  }

  return null;
}